import { createConfigParametersRepository } from '../../configParametersRepository';
import { get } from '../../utils/httpRequest';
import { updateCustomParamsAndDimensions } from '../customDimensionsRepository/storageSync';
import { createFirebaseAdapter } from '../firebase/firebaseAdapter';
import { createFirebaseClient } from '../firebase/firebaseClient';
import type { CustomParams } from '../google-analytics/ga4Types';
import { getPageLocation, gtagWrapper } from '../google-analytics/utils';
import { checkGaInit } from '../init/ga';
import { getBuyPathParam } from '../init/ga/customDimensions';
import { createRuntimeGoogleState } from '../init/ga/state';
import {
  CUSTOM_PARAMS_STORAGE_KEY,
  MKTTRACK_COOKIE_NAME,
} from '../resources/constants';
import type { CustomDimensions } from '../types';
import { identifyAnalyticsClient } from '../utils/analytics-identification';
import { handlePageError } from './error';
import { trackEvent } from './events';
import type { BundleId, FieldsObject, TrackPageOptions } from './types';

export const trackPageInGa4 = (screen_name: string) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const firebaseAdapter = createFirebaseAdapter();
  const firebaseClient = createFirebaseClient(firebaseAdapter);

  const storedCustomParams = storageClient?.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  );

  firebaseClient.trackScreenView({
    ...storedCustomParams,
    screen_name,
  });

  const page_location = getPageLocation(screen_name);
  gtagWrapper('event', 'page_view', {
    page_location,
    ...storedCustomParams,
  });
  gtagWrapper('event', 'edo_screen_view', {
    page_location,
    screen_name,
    ...storedCustomParams,
    event_callback: void identifyAnalyticsClient,
  });
};

const updatePageCustomDimensions = (params: CustomParams) => {
  const filteredDimensions = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value || value === '')
  );

  updateCustomParamsAndDimensions(filteredDimensions);
};

export const getPageCustomDimensions = async (page: string) => {
  if (page?.includes('/confirmation')) return {};
  const { getGoogleAnalyticsParameters } = createConfigParametersRepository();
  const { endpoint } = getGoogleAnalyticsParameters();
  if (!endpoint.pageCustomDimensions) return {};
  const url = `${endpoint.pageCustomDimensions}${encodeURIComponent(page)}`;
  try {
    const response: CustomDimensions = await get(url, {
      accept: 'application/json, text/javascript, */*; q=0.01',
    });
    return response;
  } catch (_) {
    return {};
  }
};

const removeAnalyticsSearchInfo = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();

  storageClient?.removeFromStorage('sessionStorage', 'analyticsSearchInfo');
};

const getMktTrackDimension = (): Pick<FieldsObject, 'mkttrack'> => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const mkttrack = storageClient?.getCookie(MKTTRACK_COOKIE_NAME) || '';
  return { mkttrack };
};

const getBundleIdFields = (): Pick<FieldsObject, 'bundle_ID'> => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const bundleId = storageClient?.getFromStorage(
    'sessionStorage',
    'bookingInfo',
    null
  ) as BundleId | null;

  if (bundleId?.shoppingBasketBundle?.id) {
    return {
      bundle_ID: bundleId.shoppingBasketBundle.id,
    };
  }
  return {};
};

const getMobileFields = (): Pick<FieldsObject, 'connection_type'> => {
  const { getGenericParameters } = createConfigParametersRepository();
  const { flow } = getGenericParameters();
  if (flow === 'mobile') {
    return {
      connection_type:
        (window.navigator.connection && window.navigator.connection.type) ||
        'unknown',
    };
  }
  return {};
};

const getFieldsObjectForPageview = (): FieldsObject => {
  return {
    ...getMktTrackDimension(),
    ...getBundleIdFields(),
    ...getMobileFields(),
  };
};

export const trackPostponedEvents = () => {
  const { getEventsQueue, emptyEventsQueue } = createRuntimeGoogleState();
  const eventsQueue = getEventsQueue();
  eventsQueue?.forEach((event) => void trackEvent(event));
  emptyEventsQueue();
};

const trackGoogleAnalyticsPage = async (
  page: string,
  options?: TrackPageOptions
) => {
  const { customDimensions } = options ?? {};
  const { setParameters } = createRuntimeGoogleState();
  removeAnalyticsSearchInfo();
  const pageBeDimensions = await getPageCustomDimensions(page);
  updatePageCustomDimensions({
    ...getFieldsObjectForPageview(),
    ...pageBeDimensions,
    ...customDimensions,
    screen_name: page,
    url: document.location.href,
    buypath: options?.funnel ? getBuyPathParam() : undefined,
  });
  trackPageInGa4(page);

  setParameters({ hasThePageBeenTracked: true });
  trackPostponedEvents();
};

export const trackPage = async (page: string, options?: TrackPageOptions) => {
  handlePageError(page);
  const { getGoogleState, setParameters } = createRuntimeGoogleState();
  const { lastTrackedPage } = getGoogleState();
  const { error } = options ?? {};
  if (lastTrackedPage === page || error) return;
  await checkGaInit();
  setParameters({ lastTrackedPage: page, hasThePageBeenTracked: false });
  await trackGoogleAnalyticsPage(page, options);
};

// Register trackPage in the runtime state so the init module can process
// postponed page views without a direct import of this file (avoids cycle).
createRuntimeGoogleState().registerTrackPage(trackPage);

export const trackPostponedPageviews = () => {
  const { getPagesQueue } = createRuntimeGoogleState();
  const pagesQueue = getPagesQueue();
  pagesQueue?.forEach((page) => void trackPage(page.page, page.options));
};
